import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export const generatePDFFromComponent = async (
  componentRef,
  filename,
  onStart = () => {},
  onComplete = () => {},
  onError = (error) => console.error("Error generating PDF:", error),
) => {
  if (!componentRef.current) {
    onError(new Error("Component reference is not available"));
    return;
  }

  onStart();

  try {
    const element = componentRef.current;

    const width = element.scrollWidth;
    const height = element.scrollHeight;

    const canvas = await html2canvas(element, {
      scale: 1.5,
      backgroundColor: "#f5f7f9",
      logging: false,
      useCORS: true,
      allowTaint: true,
      imageTimeout: 0,
      width: width,
      height: height,
    });

    const imgWidth = canvas.width;
    const imgHeight = canvas.height;

    const pdf = new jsPDF({
      orientation: imgWidth > imgHeight ? "landscape" : "portrait",
      unit: "px",
      format: [imgWidth, imgHeight],
      compress: true,
    });

    pdf.addImage(canvas.toDataURL("image/jpeg", 0.8), "JPEG", 0, 0, imgWidth, imgHeight);

    pdf.save(filename);
    onComplete();
  } catch (error) {
    onError(error);
  }
};

export const generateTestCaseReportPDF = (
  componentRef,
  testCaseName,
  runDate,
  onStart = () => {},
  onComplete = () => {},
  onError = (error) => console.error("Error generating test case report PDF:", error),
) => {
  const filename = `TestCase-Report-${testCaseName}-${runDate}.pdf`;
  generatePDFFromComponent(componentRef, filename, onStart, onComplete, onError);
};

export const generateTestSuiteReportPDF = (
  componentRef,
  testSuiteName,
  onStart = () => {},
  onComplete = () => {},
  onError = (error) => console.error("Error generating test suite report PDF:", error),
) => {
  const filename = `Execution-Report-${testSuiteName}.pdf`;
  generatePDFFromComponent(componentRef, filename, onStart, onComplete, onError);
};

export const generatePerformanceReportPDF = async (
  componentRef,
  suiteName,
  onStart = () => {},
  onComplete = () => {},
  onError = (error) => console.error("Error generating performance report PDF:", error),
) => {
  if (!componentRef.current) {
    onError(new Error("Component reference is not available"));
    return;
  }

  onStart();

  try {
    const element = componentRef.current;

    const originalStyles = {
      height: element.style.height,
      maxHeight: element.style.maxHeight,
      overflow: element.style.overflow,
      overflowY: element.style.overflowY,
      position: element.style.position,
    };

    element.style.height = "auto";
    element.style.maxHeight = "none";
    element.style.overflow = "visible";
    element.style.overflowY = "visible";
    element.style.position = "relative";

    await new Promise((resolve) => setTimeout(resolve, 300));

    const canvas = await html2canvas(element, {
      scale: 1.5,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      height: element.scrollHeight,
      width: element.scrollWidth,
      x: 0,
      y: 0,
      scrollX: 0,
      scrollY: 0,
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight,
      logging: false,
      onclone: (clonedDoc) => {
        const clonedElement = clonedDoc.querySelector("[data-pdf-content]") || clonedDoc.body;
        clonedElement.style.height = "auto";
        clonedElement.style.overflow = "visible";
      },
    });

    Object.keys(originalStyles).forEach((key) => {
      element.style[key] = originalStyles[key];
    });

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: true,
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    const margin = 5;
    const imgWidth = pdfWidth - margin * 2;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    const pageHeight = pdfHeight - margin * 2;
    let heightLeft = imgHeight;
    let position = margin;

    pdf.addImage(
      canvas.toDataURL("image/jpeg", 0.85),
      "JPEG",
      margin,
      position,
      imgWidth,
      imgHeight,
    );

    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight + margin;
      pdf.addPage();
      pdf.addImage(
        canvas.toDataURL("image/jpeg", 0.85),
        "JPEG",
        margin,
        position,
        imgWidth,
        imgHeight,
      );
      heightLeft -= pageHeight;
    }

    const timestamp = new Date().toISOString().slice(0, 10);
    const filename = `Performance-Test-Report-${suiteName || "Suite"}-${timestamp}.pdf`;

    pdf.save(filename);
    onComplete();
  } catch (error) {
    onError(error);
  }
};
